// «Используется в N блюдах» в шторке позиции (/data/stock/product-usage): в каких блюдах товар по действующим
// техкартам, сколько продано за те же дни, что история, и сколько товара ушло по техкартам — рядом с фактическим
// расходом. Для позиции без цены этот список и есть «блюда с заниженной себестоимостью» — над ним плашка;
// для полуфабриката — плашка про акты приготовления (или про отсутствие техкарты), если этого не говорит текст
// пустого списка.
import React, { useState } from 'react';
import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import { fetchStockUsage } from '../../../../services/dataService';
import { CARD_LABEL_COLOR, DIVIDER_COLOR, MUTED_TEXT_COLOR } from '../../../../styles/tokens';
import { dishesTitle, formatNorm, formatQuantity, splitDishes, usageSummary } from '../../utils/stockModel';
import { plural } from '../../../Kitchen/kitchenFormat';
import { useStockRequest } from './useStockRequest';

const FIRST_DISHES = 10;
const WARNING_COLOR = '#f0a43a';
const NOTICE_COLOR = '#ef6b6b';
const PREPARED = 'PREPARED';

// «2026-10-28» → «28.10.2026»
const asOfLabel = (iso) => (typeof iso === 'string' ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');

// Блюд нет. У полуфабриката со своей техкартой — по настройке списания в ней (own_writeoff: «Списывать ингредиенты»
// или «Списывать готовое блюдо», с учётом подразделений) на дату справочника. Это настройка пользователя, а не
// правило: текст называет её и причину минуса — как вероятную
export function emptyText(item, data) {
  if (item.product_type === PREPARED && data.own_writeoff === 'ingredients') {
    return 'В действующей техкарте полуфабриката стоит «Списывать ингредиенты»: при продаже блюд со склада уходят его '
      + 'ингредиенты, а не он сам, поэтому в разложенных техкартах блюд его нет. Минус по нему, скорее всего, — из-за '
      + 'складских документов: проверьте акты приготовления, списания и перемещения';
  }
  if (item.product_type === PREPARED && data.own_writeoff === 'whole') {
    return 'В действующей техкарте полуфабриката стоит «Списывать готовое блюдо», но в разложенных техкартах блюд его '
      + 'нет. Если минус по нему растёт, проверьте акты приготовления, списания и перемещения';
  }
  return 'В действующих техкартах блюд с этим товаром нет';
}

function Plaque({ color, children }) {
  return (
    <Box role="note" sx={{ px: 1.5, py: 1, borderRadius: '10px', backgroundColor: `${color}1f`, border: `1px solid ${color}40` }}>
      <Typography sx={{ fontSize: '13px', color }}>{children}</Typography>
    </Box>
  );
}

// Пустой список блюд объяснён текстом про настройку техкарты полуфабриката (emptyText)
const explainedEmpty = (item, data) =>
  item.product_type === PREPARED && Boolean(data?.charts_available) && !data.dishes_total
  && (data.own_writeoff === 'ingredients' || data.own_writeoff === 'whole');

// Плашки по позиции — сразу, по признакам из строки списка; когда блюда пришли, текст уточняется:
// - без цены: «в себестоимость этих блюд…» — только если блюда есть; пока не знаем или блюд нет — «числится без цены»;
// - полуфабрикат: совет про акты приготовления не дублируем, если его уже даёт текст пустого блока
export function UsagePlaques({ item, data }) {
  const dishes = Boolean(data?.charts_available && data.dishes_total);
  return (
    <>
      {item.no_price && (
        <Plaque color={NOTICE_COLOR}>
          {dishes
            ? 'Не было приходов на склад: в себестоимость этих блюд товар попадает по нулевой цене'
            : 'Не было приходов на склад: товар числится без цены'}
        </Plaque>
      )}
      {item.product_type === PREPARED && (item.no_chart || !explainedEmpty(item, data)) && (
        <Plaque color={WARNING_COLOR}>
          {item.no_chart ? 'У полуфабриката нет действующей техкарты' : 'Полуфабрикат: проверьте, оформляются ли акты приготовления'}
        </Plaque>
      )}
    </>
  );
}

function DishRow({ dish, unit, first }) {
  const labels = [
    dish.type === 'MODIFIER' && 'модификатор',
    dish.whole_writeoff && 'списывается целиком',
    dish.size_approx && 'размеры*',
  ].filter(Boolean);
  const sold = Number(dish.sold) > 0;
  return (
    <Box component="li" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.75, borderTop: first ? 'none' : `1px solid ${DIVIDER_COLOR}` }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '14px', color: '#fff', overflowWrap: 'anywhere' }}>{dish.name}</Typography>
        <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR }}>
          {[`${formatNorm(dish.norm, unit)} на 1 шт`, ...labels].join(' · ')}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: sold ? '#fff' : MUTED_TEXT_COLOR, fontVariantNumeric: 'tabular-nums' }}>
          {sold ? `ушло ${formatQuantity(dish.used, unit)}` : 'нет продаж'}
        </Typography>
        {sold && (
          <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR, fontVariantNumeric: 'tabular-nums' }}>
            продано {Number(dish.sold).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

const linkSx = { alignSelf: 'flex-start', minHeight: 36, px: 0.5, borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: CARD_LABEL_COLOR };

function DishList({ dishes, unit }) {
  return (
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {dishes.map((dish, index) => <DishRow key={dish.dish_id} dish={dish} unit={unit} first={index === 0} />)}
    </Box>
  );
}

export default function StockUsageBlock({ item, days }) {
  const { data, failed } = useStockRequest(item, fetchStockUsage, days);
  const [showAll, setShowAll] = useState(false);
  const [showUnsold, setShowUnsold] = useState(false);
  const unit = data?.unit ?? item.unit;

  let body;
  if (failed || data?.available === false) {
    body = <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>Не удалось загрузить блюда — попробуйте позже.</Typography>;
  } else if (!data) {
    body = (
      <Box aria-busy="true" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={40} />)}
      </Box>
    );
  } else if (!data.charts_available) {
    body = <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>Техкарты временно недоступны</Typography>;
  } else if (!data.dishes_total) {
    body = <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>{emptyText(item, data)}</Typography>;
  } else {
    const { sold, unsold } = splitDishes(data.dishes);
    const shown = showAll ? sold : sold.slice(0, FIRST_DISHES);
    body = (
      <>
        <Typography sx={{ fontSize: '13px', color: CARD_LABEL_COLOR }}>{usageSummary(data, days)}</Typography>
        {data.sales_available === false && (
          <Typography sx={{ fontSize: '13px', color: WARNING_COLOR }}>Продажи за период не загрузились — «ушло» не посчитано</Typography>
        )}
        {shown.length > 0 && <DishList dishes={shown} unit={unit} />}
        {sold.length > FIRST_DISHES && (
          <ButtonBase onClick={() => setShowAll((v) => !v)} aria-expanded={showAll} sx={linkSx}>
            {showAll ? 'Свернуть' : `Показать все (${sold.length})`}
          </ButtonBase>
        )}
        {unsold.length > 0 && (
          <>
            <ButtonBase onClick={() => setShowUnsold((v) => !v)} aria-expanded={showUnsold} sx={linkSx}>
              {showUnsold ? 'Скрыть блюда без продаж' : `ещё ${unsold.length} ${plural(unsold.length, ['блюдо', 'блюда', 'блюд'])} без продаж`}
            </ButtonBase>
            {showUnsold && <DishList dishes={unsold} unit={unit} />}
          </>
        )}
        <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR }}>
          по техкартам, действующим на {asOfLabel(data.charts_as_of)}
          {data.dishes.some((d) => d.size_approx) && ' · * блюда с размерами — по общей техкарте'}
        </Typography>
      </>
    );
  }

  return (
    <Box component="section" aria-label="Используется в блюдах" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <UsagePlaques item={item} data={failed ? null : data} />
      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
        {data?.charts_available && data.dishes_total ? dishesTitle(data.dishes_total) : 'Используется в блюдах'}
      </Typography>
      {body}
    </Box>
  );
}
